import { useRef, useEffect } from "react";
import { ArrowLeft, ArrowRight, QuoteIcon } from "lucide-react";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTestimonials } from "@/lib/getTestimonials";
import clientLogos from "@/assets/data/companies";

const TestimonialsSection = () => {
  const { t, language } = useLanguage();
  const testimonials = getTestimonials(language);
  const isRTL = language === "ar";
  const carouselRef = useRef(null);

  // Set RTL direction for carousel when language changes
  useEffect(() => {
    // Force embla carousel to recognize the direction change
    const timer = setTimeout(() => {
      const api = carouselRef.current?.emblaApi;
      if (api) {
        api.reInit({
          align: "start",
          loop: true,
          direction: isRTL ? "rtl" : "ltr",
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [language, isRTL]);

  return (
    <section className="py-16 md:py-24 bg-white dark:bg-nexus-dark" id="testimonials">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-nexus-gray dark:text-white mb-4">{t("reviews.title")}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t("reviews.description")}
          </p>
        </div>

        <div className="mb-16 relative px-10 md:px-16">
          {/* Navigation arrows positioned outside the carousel content */}
          <div className="absolute left-0 top-0 bottom-0 flex items-center -ml-2 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const prevButton = document.querySelector('.embla__prev') as HTMLElement;
                if (prevButton) prevButton.click();
              }}
              className="w-12 h-12 flex items-center justify-center
                bg-blue-900/70 backdrop-blur-sm
                hover:bg-blue-800 transition-colors
                rounded-full shadow-lg
                text-white
                focus:outline-none"
              aria-label="Previous slide"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          </div>
          
          <Carousel
            opts={{
              align: "start",
              loop: true,
              direction: isRTL ? "rtl" : "ltr"
            }}
            className="w-full"
            ref={carouselRef}
          >
            <CarouselContent  dir={isRTL ? "rtl" : "ltr"}>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="md:basis-1/3 lg:basis-1/3">
                  <div className="p-1">
                    <Card className={`h-full border ${testimonial.type === "client" ? "border-nexus-teal/20" : "border-nexus-yellow/20"}`}>
                      <CardContent className="p-6">
                        <div className={`w-10 h-10 rounded-full ${testimonial.type === "client" ? "bg-nexus-teal/10" : "bg-nexus-yellow/10"} flex items-center justify-center mb-4`}>
                          <QuoteIcon className={`h-5 w-5 ${testimonial.type === "client" ? "text-nexus-teal" : "text-nexus-yellow"}`} />
                        </div>
                        <blockquote className="text-gray-700 dark:text-gray-200 mb-6">
                          "{testimonial.quote}"
                        </blockquote>
                        <div className="flex items-center">
                        <Avatar className="me-3 ltr:mr-3 rtl:ml-3">
                            <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                            <AvatarFallback className="bg-gray-300 dark:bg-gray-600">
                              {testimonial.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-nexus-gray dark:text-white">{testimonial.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{testimonial.title}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* Hidden but functional carousel controls */}
            <div className="hidden">
              <CarouselPrevious className="embla__prev" />
              <CarouselNext className="embla__next" />
            </div>
          </Carousel>
          
          {/* Right arrow positioned outside the carousel content */}
          <div className="absolute right-0 top-0 bottom-0 flex items-center -mr-2 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextButton = document.querySelector('.embla__next') as HTMLElement;
                if (nextButton) nextButton.click();
              }}
              className="w-12 h-12 flex items-center justify-center
                bg-blue-900/70 backdrop-blur-sm
                hover:bg-blue-800 transition-colors
                rounded-full shadow-lg
                text-white
                focus:outline-none"
              aria-label="Next slide"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div>
          <h3 className="text-center text-xl text-gray-500 dark:text-gray-300 mb-8">{t("reviews.companiesTitle")}</h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {clientLogos.map((logo, index) => (
              <div key={index} className={`h-12 ${logo.width} rounded flex items-center justify-center`}>
                <img 
                  src={logo.image} 
                  alt={`${logo.name} logo`} 
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ))}        
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;

  